import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import t from '../../i18n/t';
import TippyTooltip from '@components/common/tooltip/TippyTooltip';
import CheckBox from '@components/common/CheckBox';
import { MenuIcon, MenuItem } from '@components/common/ContextMenu';
import { Pill, PillGroup } from '@components/course/taskEstimator/TaskEstimator.styles';
import { CustomDurationPop } from '@components/course/taskEstimator/EstimatorControls';
import { formatMinutes } from '@components/course/taskEstimator/utils';
import {
  EstimatePopupLabel,
  EstimatePopupPanel,
  estimatePopupAppendTo,
  PopupCheckLabel,
  PopupCheckRow,
  POP_OUT_MS,
  useFocusFieldOnOpen
} from '@components/task/partials/EstimatePopup';
import { formatPlanningMinutes, TASK_ESTIMATE_OPTIONS } from '@utils/taskPlanning';
import { setCategoryMinutes } from '@state/course/actions';
import { categoryTimeApi } from '@api/categoryTimeApi';
import { MediumText } from '@utils/typography';

type Props = {
  semesterId?: number;
  courseId: number;
  categoryId: number;
  minutes: number | null;
  // The save, passed in rather than dispatched here: the Task List's group header can be drawn outside the store's
  // Provider (CourseAndGroupName).
  save: typeof setCategoryMinutes.request;
  // What opens it: the time chip (Course details, the Task List's category pill), an item of the sidebar's ⋯ menu, or
  // (`anchor`) its parent, which shows it beside `anchor` while `visible` (the task category picker's ⋯ menu). `onDone`
  // is told once a time is picked or the popup is left: the menu closes, the parent stops showing it.
  variant?: 'chip' | 'menu' | 'anchor';
  onDone?: () => void;
  anchor?: React.ReactElement;
  visible?: boolean;
};

/**
 * A category's default time (Petr, 2026-10-28/29): "1h each", or a faint "Estimate" with none, in Course details and
 * in the Task List's category pill; "Set default estimate" in the sidebar category's ⋯ menu. It opens the sidebar's
 * estimate popup pieces, the presets and the time field, with "Also re-estimate the N unfinished tasks" ticked; a pick
 * saves at once. Unticked, only tasks given the category from now on take the time.
 */
const CategoryTimeChip = ({
  semesterId,
  courseId,
  categoryId,
  minutes,
  save,
  variant = 'chip',
  onDone,
  anchor,
  visible
}: Props) => {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [applyToUnfinished, setApplyToUnfinished] = useState(true);
  // The category's tasks still to do, readings left out, fetched as the popup opens; null until they're in.
  const [unfinishedCount, setUnfinishedCount] = useState<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useFocusFieldOnOpen(open, panelRef);

  useEffect(() => {
    if (!open || !semesterId) return;
    categoryTimeApi.unfinishedCounts(semesterId, courseId).then(response => {
      if (mounted.current && response.ok && response.data) setUnfinishedCount(response.data[categoryId] || 0);
    });
  }, [open, semesterId, courseId, categoryId]);

  const show = (next: boolean) => {
    setOpen(next);
    if (next) {
      setClosing(false);
      setApplyToUnfinished(true);
      setUnfinishedCount(null);
    }
  };

  // Shown by its parent.
  useEffect(() => {
    if (variant === 'anchor') show(!!visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, visible, categoryId]);

  const leave = () => {
    show(false);
    onDone?.();
  };

  const pick = (picked: number) => {
    save({
      courseId,
      categoryId,
      minutes: picked,
      // Ticked, still counting, or not shown (nothing it would change): it re-estimates whatever is still to do, and the
      // answer says how many readings linked to a book it left to their pages, for the toast after.
      applyToUnfinished
    });
    // Shrinks back into the chip, as the sidebar's popup does into its button.
    setClosing(true);
    window.setTimeout(() => mounted.current && leave(), POP_OUT_MS);
  };

  const toggleApply = () => setApplyToUnfinished(value => !value);

  const panel = (
    <EstimatePopupPanel ref={panelRef} $closing={closing}>
      {/* The question leads, the box that widens it sits right under it, then the times (Petr, 2026-10-29). Shown while
          the count is still coming, so the times don't jump down. */}
      <Heading>
        <Title>{t.CATEGORY_TIME_HOW_LONG}</Title>
        {unfinishedCount !== 0 && (
          <PopupCheckRow>
            <CheckBox checked={applyToUnfinished} onChange={toggleApply} />
            <PopupCheckLabel onClick={toggleApply}>{t.CATEGORY_TIME_ALSO_CHANGE(unfinishedCount)}</PopupCheckLabel>
          </PopupCheckRow>
        )}
      </Heading>
      <PillGroup>
        {TASK_ESTIMATE_OPTIONS.map(option => (
          <Pill key={option} type="button" $selected={option === minutes} onClick={() => pick(option)}>
            {formatPlanningMinutes(option)}
          </Pill>
        ))}
      </PillGroup>
      <CustomDurationPop value={minutes} onApply={pick} />
    </EstimatePopupPanel>
  );

  // The popup as each opener shows it; only what it stands by, where it opens, and where it's put differ.
  const popup = {
    content: panel,
    interactive: true,
    controlled: true,
    visible: open,
    onClickOutside: leave,
    className: 'estimate-popup',
    lightTheme: true,
    noPadding: true,
    arrow: false,
    width: 'none',
    appendTo: estimatePopupAppendTo,
    zIndex: 200
  };

  if (variant === 'menu') {
    return (
      <TippyTooltip
        target={
          <MenuItem onClick={() => show(!open)}>
            <MenuIcon name={'timer'} size={14} />
            <MediumText strong style={{ whiteSpace: 'nowrap' }}>
              {t.SET_DEFAULT_ESTIMATE}
            </MediumText>
          </MenuItem>
        }
        {...popup}
        position={'right-start'}
        // Out of the sidebar, which clips anything in its menu that reaches past its edge, as the class length's
        // popup beside it is (CommitmentDurationPopup).
        appendTo={() => document.getElementById('root') || document.body}
      />
    );
  }

  if (variant === 'anchor' && anchor) {
    return <TippyTooltip target={anchor} {...popup} position={'bottom-start'} />;
  }

  // Pointed at, it says the time is the category's default estimate (Petr, 2026-10-29); not while its popup is open.
  const button = (
    <TippyTooltip
      target={
        <TimeButton type="button" $empty={minutes == null} $selected={open} onClick={() => show(!open)}>
          {minutes != null ? t.CATEGORY_TIME_EACH(formatMinutes(minutes)!) : t.CATEGORY_TIME_ADD}
        </TimeButton>
      }
      content={t.CATEGORY_TIME_HINT}
      disabled={open}
      width={'220px'}
    />
  );

  return <TippyTooltip target={<Slot>{button}</Slot>} {...popup} position={'bottom-end'} />;
};

export default CategoryTimeChip;

// One anchor for the popup, around the button that carries the hover note.
const Slot = styled.span`
  display: flex;
  flex-shrink: 0;
`;

const TimeButton = styled.button<{ $empty: boolean; $selected: boolean }>`
  flex-shrink: 0;
  padding: 2px 7px;
  border: 1px solid ${({ theme, $selected }) => ($selected ? theme.primary : theme.borderColor)};
  border-radius: 999px;
  background: ${({ theme }) => theme.background};
  font-family: 'Quicksand', sans-serif;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  color: ${({ theme, $empty }) => ($empty ? theme.textColor : theme.textStrongColor)};
  opacity: ${({ $empty }) => ($empty ? 0.6 : 1)};
  transition: opacity 0.12s ease, border-color 0.12s ease, background 0.12s ease;

  &:hover {
    opacity: 1;
    border-color: ${({ theme }) => theme.primary};
    background: color-mix(in srgb, ${({ theme }) => theme.primary} 8%, ${({ theme }) => theme.background});
  }
`;

const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 4px;
`;

const Title = styled(EstimatePopupLabel)`
  font-size: 15px;
  font-weight: 700;
`;
